import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  Circle,
  Lock,
  Mail,
  Satellite,
  Smartphone,
  Radio,
  Copy,
  Check,
  MessageSquare,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import {
  useUserQuery,
  useGmailStatus,
  useApps,
  useUpdateSettings,
  useMessages,
} from '../lib/hooks';

type DeviceType = 'inreach' | 'phone' | 'zoleo';
type StepStatus = 'done' | 'active' | 'locked' | 'skipped';

const PENDING_BANNER =
  "Your account is awaiting approval — you'll get a text when you're live.";

const DEVICE_OPTIONS: { type: DeviceType; label: string; icon: React.ElementType; instructions: string }[] = [
  {
    type: 'inreach',
    label: 'Garmin InReach',
    icon: Satellite,
    instructions:
      'Create a new message to this number and send just the 6-digit code — it may take a few minutes over satellite.',
  },
  {
    type: 'phone',
    label: 'Phone (satellite SMS)',
    icon: Smartphone,
    instructions: 'Text just the 6-digit code to this number from your phone.',
  },
  {
    type: 'zoleo',
    label: 'Zoleo',
    icon: Radio,
    instructions: 'Send a new message with just the 6-digit code to this number.',
  },
];

const statusStyles: Record<StepStatus, string> = {
  done: 'border-green-200 bg-white',
  active: 'border-gray-900 bg-white',
  locked: 'border-gray-200 bg-gray-50',
  skipped: 'border-gray-200 bg-white',
};

const StepIcon: React.FC<{ status: StepStatus }> = ({ status }) => {
  if (status === 'done') {
    return <CheckCircle2 className="w-6 h-6 text-green-600" data-testid="icon-done" />;
  }
  if (status === 'locked') {
    return <Lock className="w-5 h-5 text-gray-400" data-testid="icon-locked" />;
  }
  return <Circle className="w-6 h-6 text-gray-900" data-testid="icon-active" />;
};

interface StepCardProps {
  step: number;
  title: string;
  status: StepStatus;
  children?: React.ReactNode;
}

const StepCard: React.FC<StepCardProps> = ({ step, title, status, children }) => (
  <div
    data-testid={`step-${step}`}
    data-status={status}
    className={`rounded-lg border p-6 ${statusStyles[status]}`}
  >
    <div className="flex items-center">
      <StepIcon status={status} />
      <h3 className="ml-3 text-lg font-medium text-gray-900">
        Step {step}: {title}
      </h3>
    </div>
    {status !== 'locked' && children && <div className="mt-4 ml-9">{children}</div>}
  </div>
);

const Setup: React.FC = () => {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const { data: user } = useUserQuery(3000);
  const { data: gmailStatus } = useGmailStatus();
  const { data: apps } = useApps();
  const { data: messagesData } = useMessages(3, 5000);
  const updateSettings = useUpdateSettings();

  const [gmailSkipped, setGmailSkipped] = useState(false);
  const [gmailError, setGmailError] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<DeviceType | null>(null);
  const [pairingCode, setPairingCode] = useState<string | undefined>(undefined);
  const [pairingCodeExpiresAt, setPairingCodeExpiresAt] = useState<Date | null>(null);
  const [pairingLoading, setPairingLoading] = useState(false);
  const [pairingError, setPairingError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!pairingCodeExpiresAt) return;

    const updateTimer = () => {
      const diff = pairingCodeExpiresAt.getTime() - Date.now();
      if (diff <= 0) {
        setTimeRemaining('Expired');
        return;
      }
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [pairingCodeExpiresAt]);

  useEffect(() => {
    if (user?.phoneVerified) {
      refreshUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.phoneVerified]);

  const handleConnectGmail = async () => {
    setGmailError(null);
    try {
      const response = await api.get('/gmail/auth');

      // Redirect to Google OAuth
      window.location.href = response.data.authUrl;
    } catch (error) {
      console.error('Failed to get Gmail auth URL:', error);
      setGmailError('Failed to connect Gmail. Please try again.');
    }
  };

  const handleGoToDashboard = async () => {
    await refreshUser();
    navigate('/dashboard');
  };

  const handleGetPairingCode = async () => {
    setPairingError(null);
    setPairingLoading(true);
    try {
      const { data } = await api.post('/auth/generate-pairing-code');
      setPairingCode(data.pairingCode);
      if (data.pairingCodeExpiresAt) {
        setPairingCodeExpiresAt(new Date(data.pairingCodeExpiresAt));
      }
    } catch (error) {
      console.error('Failed to generate pairing code:', error);
      setPairingError('Failed to generate pairing code. Please try again.');
    } finally {
      setPairingLoading(false);
    }
  };

  const handleCopy = (value: string) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(value);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSelectDevice = (deviceType: DeviceType) => {
    setSelectedDevice(deviceType);
    updateSettings.mutate({ device_type: deviceType });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-sm text-gray-500">Loading your account…</p>
      </div>
    );
  }

  const gmailDone = !!gmailStatus?.connected;
  const phoneDone = !!user.phoneVerified;
  const approvalDone = user.approvalStatus === 'approved' || (user.usageQuota ?? 0) > 0;
  const serviceNumber = apps?.serviceNumber;
  const messages = messagesData?.messages || [];

  let currentStep = 5;
  if (!gmailDone && !gmailSkipped) {
    currentStep = 2;
  } else if (!phoneDone) {
    currentStep = 3;
  } else if (!approvalDone) {
    currentStep = 4;
  }

  const status2: StepStatus = gmailDone ? 'done' : gmailSkipped ? 'skipped' : currentStep === 2 ? 'active' : 'locked';
  const status3: StepStatus = phoneDone ? 'done' : currentStep === 3 ? 'active' : 'locked';
  const status4: StepStatus = approvalDone ? 'done' : currentStep === 4 ? 'active' : 'locked';
  const status5: StepStatus = phoneDone && approvalDone ? 'active' : 'locked';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center mb-8">
          <Satellite className="w-7 h-7 text-gray-900 mr-2" />
          <h1 className="text-2xl font-bold text-gray-900">Get set up</h1>
        </div>

        <div className="space-y-4">
          <StepCard step={1} title="Sign in" status="done">
            <p className="text-sm text-gray-600">Signed in as {user.email}</p>
          </StepCard>

          <StepCard step={2} title="Connect Gmail" status={status2}>
            {status2 === 'done' && (
              <p className="text-sm text-gray-600">Gmail connected.</p>
            )}
            {status2 === 'skipped' && (
              <p className="text-sm text-gray-600">
                Skipped for now — connect anytime from Settings.
              </p>
            )}
            {status2 === 'active' && (
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  Connect Gmail to enable email commands over SMS.
                </p>
                <div className="flex items-center gap-4">
                  <button
                    onClick={handleConnectGmail}
                    className="inline-flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Connect Gmail
                  </button>
                  <button
                    onClick={() => setGmailSkipped(true)}
                    className="text-sm font-medium text-gray-500 hover:text-gray-700"
                  >
                    Skip for now
                  </button>
                </div>
                {gmailError && (
                  <p className="mt-2 text-sm text-red-600">{gmailError}</p>
                )}
              </div>
            )}
          </StepCard>

          <StepCard step={3} title="Pair your device" status={status3}>
            {status3 === 'done' && (
              <p className="text-sm text-gray-600">
                Device paired. Phone: {user.phoneNumber}
              </p>
            )}
            {status3 === 'active' && (
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  Choose the device you'll be texting from.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
                  {DEVICE_OPTIONS.map(option => {
                    const Icon = option.icon;
                    const selected = selectedDevice === option.type;
                    return (
                      <button
                        key={option.type}
                        onClick={() => handleSelectDevice(option.type)}
                        disabled={updateSettings.isLoading}
                        className={`flex flex-col items-center text-center px-4 py-4 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          selected
                            ? 'border-gray-900 bg-gray-50'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-6 h-6 text-gray-700 mb-2" />
                        <span className="text-sm font-medium text-gray-900">{option.label}</span>
                      </button>
                    );
                  })}
                </div>

                {serviceNumber && (
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
                      Our number
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="text-2xl font-semibold text-gray-900">{serviceNumber}</span>
                      <button
                        onClick={() => handleCopy(serviceNumber)}
                        className="text-gray-400 hover:text-gray-600"
                        aria-label="Copy service number"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                )}

                {pairingError && (
                  <p className="text-sm text-red-600 mb-3">{pairingError}</p>
                )}

                {!pairingCode ? (
                  <button
                    onClick={handleGetPairingCode}
                    disabled={pairingLoading}
                    className="inline-flex items-center px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    {pairingLoading ? 'Generating…' : 'Get pairing code'}
                  </button>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl font-mono font-semibold text-gray-900">
                        {pairingCode}
                      </span>
                      {timeRemaining && (
                        <span
                          className={`text-xs font-medium px-2 py-1 rounded ${
                            timeRemaining === 'Expired'
                              ? 'bg-red-50 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {timeRemaining === 'Expired' ? 'Expired' : `Expires in ${timeRemaining}`}
                        </span>
                      )}
                    </div>
                    {selectedDevice && (
                      <p className="text-sm text-gray-600">
                        {DEVICE_OPTIONS.find(o => o.type === selectedDevice)?.instructions}
                      </p>
                    )}
                    <p className="mt-3 text-xs text-gray-400">
                      This page updates automatically once we hear from your device.
                    </p>
                  </div>
                )}
              </div>
            )}
          </StepCard>

          <StepCard step={4} title="Approval" status={status4}>
            {status4 === 'done' && (
              <p className="text-sm text-gray-600">Your account is approved and ready to go.</p>
            )}
            {status4 !== 'done' && status4 !== 'locked' && (
              <div className="bg-amber-50 text-amber-800 rounded-md p-4 text-sm">
                {PENDING_BANNER}
              </div>
            )}
          </StepCard>

          <StepCard step={5} title="Test drive" status={status5}>
            {status5 === 'active' && (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Text <span className="font-mono font-semibold">WX</span> to{' '}
                  <span className="font-mono font-semibold">{serviceNumber}</span> to get a
                  weather forecast.
                </p>
                <div className="bg-white rounded-lg border border-gray-200">
                  <div className="px-4 py-3 border-b border-gray-200 flex items-center">
                    <MessageSquare className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-sm font-medium text-gray-900">Latest messages</span>
                  </div>
                  {messages.length > 0 ? (
                    <div className="divide-y divide-gray-200">
                      {messages.map((message: any) => (
                        <div key={message.id} className="px-4 py-3">
                          <p className="text-sm text-gray-900">{message.content}</p>
                          {message.response && (
                            <p className="text-sm text-gray-500 mt-1">{message.response}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-6 text-center text-sm text-gray-500">
                      No messages yet — send your first text to see it here.
                    </div>
                  )}
                </div>
              </div>
            )}
          </StepCard>
        </div>

        {phoneDone && (
          <div className="mt-6">
            <button
              type="button"
              onClick={handleGoToDashboard}
              className="w-full inline-flex items-center justify-center px-4 py-3 bg-gray-900 text-white text-base font-semibold rounded-md hover:bg-gray-800 transition-colors"
            >
              Go to dashboard
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Setup;
